import React, { forwardRef } from 'react';
import { Flex, View } from '@smarterservices/smarter-ui';

/**
 * HeaderBar Component
 * 
 * A flexible header bar component with left and right content areas.
 * Provides consistent spacing and alignment across the application.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.leftContent - Content to display on the left side
 * @param {React.ReactNode} props.rightContent - Content to display on the right side
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Inline styles
 * @param {string} props.background - Background color variant
 * @param {string} props.padding - Padding size
 * @param {string} props.height - Height of the header bar
 * @param {boolean} props.shadow - Whether to show shadow
 * @param {boolean} props.border - Whether to show bottom border
 * @param {React.Ref} ref - Forwarded ref to the root element
 */
const HeaderBar = forwardRef(({
  leftContent,
  rightContent,
  className = '',
  style = {},
  background = 'primary-inverse',
  padding = null,
  margin = null,
  ...rest
}, ref) => {

  return (
    <View 
      as="div" 
      borderRadius='medium' 
      margin={margin} 
      background={background}
      ref={ref}
      style={style}
      {...rest}
    >
      <Flex
        as="div"
        direction="row"
        justifyItems="space-between"
        alignItems="center"      
        padding={padding}
        className={`header-bar ${className}`}
      >
        <Flex.Item shouldGrow={false} shouldShrink={false}>
            {leftContent}
        </Flex.Item>
        
        <Flex.Item shouldGrow={true} shouldShrink={true}>
          {/* Spacer to push content to edges */}
        </Flex.Item>
        
        <Flex.Item shouldGrow={false} shouldShrink={false}>
            {rightContent}
        </Flex.Item>
      </Flex>
    </View>
  );
});

HeaderBar.displayName = 'HeaderBar';

export default HeaderBar;
